import React, { useState, useEffect, useRef } from "react"
import { Input, Box } from "@chakra-ui/react"
import { BadgeTextData } from "sharedTypes/badge"

type Props = {
  data: BadgeTextData
  onSave: (newText: string) => void
  onCancel: () => void
  onChangeRealtime?: (newText: string) => void
  style?: React.CSSProperties
}

const InlineTextEditor = ({
  data, onSave, onCancel, onChangeRealtime, style,
}: Props) => {
  const [text, setText] = useState(data.text || "")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      const { length } = inputRef.current.value
      inputRef.current.setSelectionRange(length, length)
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSave()
    } else if (e.key === "Escape") {
      e.preventDefault()
      onCancel()
    }
  }

  const handleSave = () => {
    onSave(text)
  }

  const handleBlur = () => {
    handleSave()
  }

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems={
        data.verticalAlign === "top"
          ? "flex-start"
          : data.verticalAlign === "bottom"
            ? "flex-end"
            : "center"
      }
      justifyContent={
        data.align === "center"
          ? "center"
          : data.align === "right"
            ? "flex-end"
            : data.align === "justify"
              ? "center"
              : "flex-start"
      }
      zIndex={1000}
      p={0}
      onClick={(e) => {
        e.stopPropagation()
      }}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
      style={{ userSelect: "text" }}
    >
      <Input
        ref={inputRef}
        value={text}
        onChange={(e) => {
          const newText = e.target.value
          setText(newText)
          if (onChangeRealtime) {
            onChangeRealtime(newText)
          }
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
        }}
        variant="unstyled"
        style={{
          width: data.align === "justify" ? "100%" : "auto",
          margin: "0",
          padding: "0",
          border: "none",
          outline: "none",
          boxShadow: "none",
          borderRadius: "0",
          lineHeight: "1",
          fontSize: "inherit",
          fontFamily: "inherit",
          fontWeight: "inherit",
          color: "inherit",
          textAlign: "inherit",
          fontStyle: "inherit",
          textDecoration: "inherit",
          textTransform: "inherit",
          backgroundColor: "transparent",
          minHeight: "auto",
          height: "auto",
          whiteSpace: "pre-wrap", // Match Text component whitespace handling
          userSelect: "text", // Allow text selection
          WebkitUserSelect: "text", // Safari
          MozUserSelect: "text", // Firefox
          msUserSelect: "text", // IE/Edge
          ...style,
        }}
        _focus={{
          border: "none",
          outline: "none",
          boxShadow: "none",
          padding: "0",
        }}
        _hover={{
          border: "none",
          outline: "none",
          boxShadow: "none",
          padding: "0",
        }}
        placeholder="Enter text..."
      />
    </Box>
  )
}

export default InlineTextEditor
